import { useEffect, useMemo, useRef } from "react";
import {
  useDispatch as _useDispatch,
  useSelector as _useSelector,
} from "react-redux";

import type { AppDispatch, RootState } from "@src/store/rootState";

export const useDispatch = _useDispatch.withTypes<AppDispatch>();
export const useSelector = _useSelector.withTypes<RootState>();

export const useCachedSelector = <T>(
  selector: (state: RootState) => T | undefined
): T | undefined => {
  const value = useSelector(selector);
  const ref = useRef(value);

  useEffect(() => {
    if (value !== undefined) {
      ref.current = value;
    }
  }, [value]);

  return value;
};

// https://redux.js.org/usage/deriving-data-selectors#selector-factories
export const useSelectorFactory = <Params extends any[], Result>(
  selectorFactory: (...args: Params) => (state: RootState) => Result,
  ...args: Params
) => {
  // eslint-disable-next-line react-compiler/react-compiler
  const selector = useMemo(() => selectorFactory(...args), args);
  return useSelector(selector);
};
